if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]:not([data-qva-buy-now])');
        this.buyNowButton = this.querySelector('[data-qva-buy-now]');
        this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButtonText = this.submitButton.querySelector('span');
        this.requestInFlight = false;
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submittedButton = evt.submitter instanceof HTMLElement ? evt.submitter : this.submitButton;
        const isBuyNow = submittedButton?.matches('[data-qva-buy-now]') || false;
        if (
          this.requestInFlight ||
          !submittedButton ||
          submittedButton.disabled ||
          submittedButton.getAttribute('aria-disabled') === 'true'
        )
          return;

        const requestId =
          submittedButton.dataset.qvaBuyNowRequestId || this.form.dataset.qvaBuyNowRequestId || '';
        const activeSpinner = submittedButton.querySelector('.loading__spinner');
        const cartMutationToken = `add:${Date.now()}:${Math.random().toString(36).slice(2)}`;
        let cartAddState = 'pending';
        let cartReconciliationRequired = false;
        let cartReconciliationSignaled = false;
        this.requestInFlight = true;
        this.dispatchCartMutation('qva:cart-mutation-start', cartMutationToken, 'add');

        this.handleErrorMessage();

        this.submitButtons.forEach((button) => button.setAttribute('aria-disabled', 'true'));
        submittedButton.classList.add('loading');
        activeSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        Promise.resolve()
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response && response.status) {
              cartAddState = 'rejected';
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              if (isBuyNow) this.dispatchBuyNowEvent('qva:buy-now-error', requestId, formData.get('id'));
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cartAddResponseIsUsable(response)) {
              throw new Error('ambiguous-cart-add-response');
            }

            cartAddState = 'accepted';
            if (!this.cart) {
              cartReconciliationRequired = true;
              this.lockSubmitButtonsForCartReconciliation();
              if (isBuyNow && !cartReconciliationSignaled) {
                cartReconciliationSignaled = true;
                this.dispatchBuyNowEvent('qva:buy-now-cart-reconcile', requestId, formData.get('id'));
              }
              this.reconcileCartAfterAmbiguousAdd();
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const subscribersSettled = !this.error
              ? publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
              : Promise.resolve();
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const renderUpdatedCart = () => {
              let renderPromise;
              CartPerformance.measure('add:paint-updated-sections', () => {
                renderPromise = this.renderCartSilently(response, { showConfirmation: !isBuyNow });
              });

              return Promise.resolve(renderPromise).then(() => {
                if (!isBuyNow) return;
                if (requestId) {
                  this.dispatchBuyNowEvent('qva:buy-now-cart-ready', requestId, formData.get('id'));
                } else {
                  this.continueNativeBuyNow();
                }
              });
            };

            if (quickAddModal) {
              const modalClosed = new Promise((resolve) => {
                let settled = false;
                const finish = () => {
                  if (settled) return;
                  settled = true;
                  window.clearTimeout(fallbackTimer);
                  document.body.removeEventListener('modalClosed', finish);
                  setTimeout(resolve);
                };
                const fallbackTimer = window.setTimeout(finish, 3000);
                document.body.addEventListener('modalClosed', finish, { once: true });
              });
              quickAddModal.hide(true);
              return Promise.all([modalClosed.then(renderUpdatedCart), subscribersSettled]);
            }

            return Promise.all([renderUpdatedCart(), subscribersSettled]);
          })
          .catch((e) => {
            console.error(e);
            // Once /cart/add has been dispatched, a transport failure, invalid
            // JSON, or cart-render failure is ambiguous: Shopify may already
            // have committed the line item. Never emit qva:buy-now-error here;
            // the gateway would release its correlation and invite a second
            // add. Keep every submit control locked and reconcile on /cart.
            if (cartAddState === 'rejected') return;
            cartReconciliationRequired = true;
            this.lockSubmitButtonsForCartReconciliation();
            if (isBuyNow && !cartReconciliationSignaled) {
              cartReconciliationSignaled = true;
              this.dispatchBuyNowEvent('qva:buy-now-cart-reconcile', requestId, formData.get('id'));
            }
            this.reconcileCartAfterAmbiguousAdd();
          })
          .finally(() => {
            try {
              this.requestInFlight = cartReconciliationRequired;
              submittedButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!cartReconciliationRequired && !this.error) {
                this.submitButtons.forEach((button) => button.removeAttribute('aria-disabled'));
              }
              activeSpinner?.classList.add('hidden');
              if (
                !cartReconciliationRequired &&
                requestId &&
                this.form.dataset.qvaBuyNowRequestId === requestId
              ) {
                delete this.form.dataset.qvaBuyNowRequestId;
              }
            } finally {
              this.dispatchCartMutation('qva:cart-mutation-end', cartMutationToken, 'add');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      dispatchCartMutation(name, token, source) {
        try {
          document.dispatchEvent(new CustomEvent(name, { detail: { token, source } }));
        } catch (e) {
          // Product adds must keep working when the optional gateway is absent.
        }
      }

      dispatchBuyNowEvent(name, requestId, variantId) {
        try {
          document.dispatchEvent(
            new CustomEvent(name, {
              detail: {
                source: 'product-form',
                requestId: String(requestId || ''),
                variantId: String(variantId || ''),
                productVariantId: String(variantId || ''),
              },
            })
          );
        } catch (error) {
          // Cart truth and the native add path must not depend on the gateway.
        }
      }

      cartAddResponseIsUsable(response) {
        if (!response || typeof response !== 'object' || Array.isArray(response)) return false;

        const lineIdentityIsUsable = (value) => {
          if (typeof value === 'number') return Number.isFinite(value) && value > 0;
          if (typeof value === 'string') return value.trim() !== '' && value.trim() !== '0';
          return false;
        };

        const hasAddedLine =
          lineIdentityIsUsable(response.id) ||
          (Array.isArray(response.items) &&
            response.items.some((item) => item && typeof item === 'object' && lineIdentityIsUsable(item.id)));
        if (!hasAddedLine) return false;

        if (!this.cart) return true;
        if (!response.sections || typeof response.sections !== 'object' || Array.isArray(response.sections)) {
          return false;
        }

        const sectionNames = Object.keys(response.sections);
        return (
          sectionNames.length > 0 &&
          sectionNames.every((sectionName) => typeof response.sections[sectionName] === 'string')
        );
      }

      lockSubmitButtonsForCartReconciliation() {
        this.requestInFlight = true;
        this.submitButtons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('data-qva-cart-reconcile-pending', 'true');
        });
      }

      reconcileCartAfterAmbiguousAdd() {
        this.handleErrorMessage(
          'Yêu cầu thêm sản phẩm có thể đã được ghi nhận. Đừng bấm thêm lần nữa; đang mở giỏ hàng để kiểm tra.'
        );

        if (this.navigateToCartSafely()) return;
        // navigateToCartSafely only declines while protected Wallet work is
        // active. Always register the deferred reconciliation: Wallet could
        // become idle synchronously in another navigation-blocked listener.
        this.queueCartReconciliationAfterWallet();
      }

      queueCartReconciliationAfterWallet() {
        if (this.pendingCartReconciliation) return;

        const operation = {
          done: false,
          checkQueued: false,
          observer: null,
        };
        this.pendingCartReconciliation = operation;

        const cleanup = () => {
          document.removeEventListener('qva:cart-mutation-end', onMutationEnd);
          operation.observer?.disconnect();
          if (this.pendingCartReconciliation === operation) this.pendingCartReconciliation = null;
        };
        const check = () => {
          operation.checkQueued = false;
          if (operation.done || this.walletMutationIsBusy()) return;
          operation.done = true;
          cleanup();
          this.navigateToCartSafely();
        };
        const queueCheck = () => {
          if (operation.done || operation.checkQueued) return;
          operation.checkQueued = true;
          window.setTimeout(check, 0);
        };
        const onMutationEnd = () => queueCheck();

        document.addEventListener('qva:cart-mutation-end', onMutationEnd);
        if (typeof MutationObserver === 'function' && document.documentElement) {
          operation.observer = new MutationObserver(queueCheck);
          operation.observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-qva-wallet-mutation-busy'],
          });
        }

        // Cover the race where Wallet became idle between the first check and
        // listener registration. The deferred re-check also lets an immediate
        // follow-up protected token start before navigation is considered.
        queueCheck();
      }

      continueNativeBuyNow() {
        const checkoutButton =
          document.querySelector('cart-drawer [name="checkout"]:not([disabled])') ||
          document.querySelector('cart-notification [name="checkout"]:not([disabled])') ||
          document.querySelector('main [name="checkout"]:not([disabled])');

        if (checkoutButton) {
          checkoutButton.click();
        } else {
          this.navigateToCartSafely();
        }
      }

      walletMutationIsBusy() {
        const element = document.documentElement;
        if (!element?.hasAttribute('data-qva-wallet-mutation-busy')) return false;
        const value = String(element.getAttribute('data-qva-wallet-mutation-busy') || '').toLowerCase();
        return value !== 'false' && value !== '0' && value !== 'off';
      }

      navigateToCartSafely() {
        if (this.walletMutationIsBusy()) {
          this.handleErrorMessage(
            'Ví điểm vẫn đang xử lý. Sản phẩm đã được thêm; vui lòng chờ rồi mở giỏ hàng để tiếp tục.'
          );
          try {
            document.dispatchEvent(
              new CustomEvent('qva:wallet-navigation-blocked', { detail: { source: 'product-form' } })
            );
          } catch (error) {
            // The accepted cart add must not depend on the optional gateway.
          }
          return false;
        }

        try {
          window.location.assign(window.routes.cart_url);
        } catch (navigationError) {
          window.location.href = window.routes.cart_url;
        }
        return true;
      }

      renderCartSilently(response, options = {}) {
        const cart = this.cart;
        if (!cart || typeof cart.renderContents !== 'function') {
          if (options.showConfirmation !== false) this.showAddedConfirmation();
          return Promise.resolve();
        }

        return Promise.resolve(cart.renderContents(response, { open: false })).then(() => {
          if (options.showConfirmation !== false) this.showAddedConfirmation();
        });
      }

      showAddedConfirmation() {
        let toast = document.getElementById('qva-add-confirmation');

        if (!document.getElementById('qva-add-confirmation-styles')) {
          const style = document.createElement('style');
          style.id = 'qva-add-confirmation-styles';
          style.textContent = '#qva-add-confirmation{position:fixed;right:2rem;bottom:2rem;z-index:2147483000;display:grid;grid-template-columns:minmax(0,1fr) auto auto;align-items:center;gap:1.2rem;width:min(44rem,calc(100vw - 4rem));padding:1.4rem 1.6rem;border:1px solid rgba(45,106,45,.22);border-radius:1.2rem;background:rgb(var(--color-background,255,255,255));color:rgb(var(--color-foreground,18,18,18));box-shadow:0 1rem 3rem rgba(0,0,0,.18);opacity:0;transform:translateY(1.2rem);visibility:hidden;pointer-events:none;transition:opacity 180ms ease,transform 180ms ease,visibility 180ms ease}#qva-add-confirmation.is-visible{opacity:1;transform:translateY(0);visibility:visible;pointer-events:auto}.qva-add-confirmation__copy{display:grid;gap:.2rem;min-width:0;line-height:1.35}.qva-add-confirmation__copy strong{color:#2d6a2d;font-size:1.5rem}.qva-add-confirmation__copy span{font-size:1.3rem;opacity:.75}.qva-add-confirmation__view{min-height:4rem;padding:.8rem 1.4rem;border:0;border-radius:.8rem;background:#2d6a2d;color:#fff;font:inherit;font-weight:700;white-space:nowrap;cursor:pointer}.qva-add-confirmation__close{display:grid;place-items:center;width:3.2rem;height:3.2rem;padding:0;border:0;background:transparent;color:currentColor;font-size:2.2rem;line-height:1;cursor:pointer}@media screen and (max-width:749px){#qva-add-confirmation{right:1rem;bottom:calc(7.2rem + env(safe-area-inset-bottom));left:1rem;width:auto;gap:.8rem;padding:1.2rem}.qva-add-confirmation__copy span{display:none}.qva-add-confirmation__view{min-height:3.8rem;padding-inline:1.1rem}}@media (prefers-reduced-motion:reduce){#qva-add-confirmation{transition:none}}';
          document.head.appendChild(style);
        }

        if (!toast) {
          toast = document.createElement('div');
          toast.id = 'qva-add-confirmation';
          toast.setAttribute('role', 'status');
          toast.setAttribute('aria-live', 'polite');
          toast.innerHTML = '<div class="qva-add-confirmation__copy"><strong>✓ Đã thêm vào giỏ hàng</strong><span>Bạn có thể tiếp tục mua sắm.</span></div><button class="qva-add-confirmation__view" type="button" data-qva-view-cart>Xem giỏ hàng</button><button class="qva-add-confirmation__close" type="button" aria-label="Đóng thông báo">×</button>';
          document.body.appendChild(toast);

          toast.querySelector('.qva-add-confirmation__close').addEventListener('click', () => {
            toast.classList.remove('is-visible');
          });
        }

        const viewCartButton = toast.querySelector('[data-qva-view-cart]');
        viewCartButton.onclick = () => {
          toast.classList.remove('is-visible');
          setTimeout(() => {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open(viewCartButton);
            } else {
              window.location = window.routes.cart_url;
            }
          });
        };

        clearTimeout(toast.hideTimer);
        toast.classList.remove('is-visible');
        requestAnimationFrame(() => toast.classList.add('is-visible'));
        toast.hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const cartReconciliationPending = this.submitButtons.some(
          (button) => button.getAttribute('data-qva-cart-reconcile-pending') === 'true'
        );
        if (!disable && cartReconciliationPending) {
          this.submitButtons.forEach((button) => {
            button.disabled = true;
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
          });
          return;
        }

        if (disable) {
          this.submitButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtons.forEach((button) => button.removeAttribute('disabled'));
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
